import { useState } from 'react';
import { useWorkbook } from '@/context/WorkbookContext';
import { 
  WorkbookPage, 
  RuleHeader, 
  SectionTitle, 
  Paragraph, 
  Callout,
  ExampleBox,
  StepList,
  WorksheetInput,
  CheckboxItem,
  AffirmationBox,
  RecapBox,
  OptionalSection 
} from '@/components/WorkbookPage';
import { Car, AlertTriangle, ShoppingBag } from 'lucide-react';

export const Rule11Page = () => {
  const { formData, updateFormData } = useWorkbook();
  const data = formData.rule11 || {};
  
  const updateData = (field, value) => {
    updateFormData('rule11', { ...data, [field]: value });
  };
  
  return (
    <WorkbookPage pageId="rule-11" prevPage="rule-10" nextPage="section-4-divider">
      <RuleHeader ruleNumber="11" title="Smart Borrowing: Cars & BNPL" section="growth" />
      
      <SectionTitle>What This Rule Means</SectionTitle>
      <Paragraph>
        Some borrowing is necessary (like a car loan to get to work), but many people borrow 
        unwisely and end up worse off. This rule covers two common areas: car loans and 
        Buy Now, Pay Later (BNPL)—and how to use them smartly or avoid the traps.
      </Paragraph>
      
      {/* Car Loans Section */}
      <div className="my-8">
        <div className="flex items-center gap-3 mb-4">
          <Car className="h-6 w-6 text-primary" />
          <h2 className="text-xl font-serif font-bold">Car Loans</h2>
        </div>
        
        <Paragraph>
          A car might be necessary, but an expensive car loan can wreck your finances. 
          Cars lose value fast (depreciation), so borrowing too much means owing more 
          than the car is worth.
        </Paragraph>
        
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-5 my-6">
          <h4 className="font-semibold mb-3">The 20/4/10 Rule for Car Buying</h4>
          <div className="space-y-2 text-sm">
            <p><strong>20%</strong> — Put at least 20% down</p>
            <p><strong>4</strong> — Finance for no more than 4 years</p>
            <p><strong>10%</strong> — Total car costs (payment + insurance) ≤ 10% of gross income</p>
          </div>
        </div>
        
        <ExampleBox title="Car Loan Example">
          <p><strong>Income:</strong> $4,000/month gross</p>
          <p><strong>10% max for car:</strong> $400/month total</p>
          <p><strong>Insurance:</strong> $150/month</p>
          <p><strong>Max car payment:</strong> $250/month</p>
          <p className="mt-3 text-sm text-muted-foreground">
            This likely means a car around $10,000-12,000, not $30,000.
          </p>
        </ExampleBox>
        
        <Callout type="warning" title="Car Loan Red Flags">
          <ul className="space-y-1 text-sm">
            <li>• Loan terms longer than 5 years (you'll be underwater)</li>
            <li>• Interest rates above 10% (shop around!)</li>
            <li>• "Buy here, pay here" lots (often predatory)</li>
            <li>• Rolling negative equity into a new loan</li>
            <li>• Adding extras like extended warranties at the dealer</li>
          </ul>
        </Callout>
      </div>
      
      {/* BNPL Section */}
      <div className="my-8">
        <div className="flex items-center gap-3 mb-4">
          <ShoppingBag className="h-6 w-6 text-primary" />
          <h2 className="text-xl font-serif font-bold">Buy Now, Pay Later (BNPL)</h2>
        </div>
        
        <Paragraph>
          Services like Afterpay, Klarna, and Affirm let you split purchases into installments. 
          They seem convenient, but they can lead to overspending and debt accumulation.
        </Paragraph>
        
        <div className="grid md:grid-cols-2 gap-4 my-6">
          <div className="bg-success/5 border border-success/20 rounded-lg p-4">
            <h4 className="font-semibold text-success mb-2">When BNPL Might Be OK</h4>
            <ul className="text-sm space-y-1">
              <li>• No interest/fees (read terms carefully)</li>
              <li>• You have the money, just spreading it out</li>
              <li>• For a planned, budgeted purchase</li>
              <li>• You pay on time every time</li>
            </ul>
          </div>
          
          <div className="bg-destructive/5 border border-destructive/20 rounded-lg p-4">
            <h4 className="font-semibold text-destructive mb-2">When BNPL Is Dangerous</h4>
            <ul className="text-sm space-y-1">
              <li>• You wouldn't buy it if you had to pay now</li>
              <li>• You have multiple BNPL payments running</li>
              <li>• You miss payments (late fees add up)</li>
              <li>• It's a "want," not a "need"</li>
            </ul>
          </div>
        </div>
        
        <div className="bg-warning/5 border border-warning/20 rounded-lg p-5 my-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-warning shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold mb-2">The BNPL Trap</h4>
              <p className="text-sm text-muted-foreground">
                Because BNPL makes purchases "feel" cheaper, people often buy more than they should. 
                Before you know it, you have 5 different BNPL payments totaling $500/month—money 
                you don't have. Many BNPL services also charge late fees and may report to credit 
                bureaus, hurting your score.
              </p>
            </div>
          </div>
        </div>
        
        <ExampleBox title="BNPL Reality Check">
          <p><strong>The purchase:</strong> $200 shoes, 4 payments of $50</p>
          <p><strong>The question:</strong> Would you buy them if you had to pay $200 today?</p>
          <p><strong>If no:</strong> You probably can't afford them. BNPL isn't making it affordable—
          it's just hiding the cost.</p>
          <p className="mt-3 text-sm text-muted-foreground">
            The best BNPL strategy: Only use it for purchases you'd make anyway, and could pay 
            cash for today.
          </p>
        </ExampleBox>
      </div>
      
      <StepList steps={[
        "For car loans: Follow the 20/4/10 rule.",
        "Get pre-approved for a car loan BEFORE going to the dealer.",
        "Shop interest rates—banks, credit unions, not just the dealer.",
        "For BNPL: Track all active BNPL payments in one place.",
        "Before using BNPL, ask: Would I buy this with cash today?",
        "Avoid stacking multiple BNPL payments.",
        "If you miss a BNPL payment, prioritize paying it to avoid fees."
      ]} />
      
      <div className="bg-muted/30 rounded-xl p-6 my-8 border border-border">
        <h3 className="font-serif font-bold text-lg mb-6">📝 Your Smart Borrowing Worksheet</h3>
        
        <div className="space-y-6">
          <div className="border-b border-border pb-6">
            <h4 className="font-semibold mb-4">Car Loan (if applicable)</h4>
            <div className="grid md:grid-cols-2 gap-4">
              <WorksheetInput
                label="Current car loan balance:"
                placeholder="$___"
                value={data.carLoanAmount || ''}
                onChange={(v) => updateData('carLoanAmount', v)}
              />
              <WorksheetInput
                label="Monthly car payment:"
                placeholder="$___"
                value={data.carPayment || ''}
                onChange={(v) => updateData('carPayment', v)}
              />
            </div>
            <WorksheetInput
              label="Interest rate (APR):"
              placeholder="___%"
              value={data.carAPR || ''}
              onChange={(v) => updateData('carAPR', v)}
            />
          </div>
          
          <div>
            <h4 className="font-semibold mb-4">BNPL Payments (list all active)</h4>
            <div className="space-y-3">
              {[1, 2, 3, 4].map((num) => (
                <div key={num} className="grid grid-cols-3 gap-2">
                  <input
                    type="text"
                    className="col-span-2 border-b border-dashed border-border bg-transparent py-2 focus:outline-none"
                    placeholder={`BNPL ${num}: Item/Service`}
                    value={data[`bnpl${num}Item`] || ''}
                    onChange={(e) => updateData(`bnpl${num}Item`, e.target.value)}
                  />
                  <input
                    type="text"
                    className="border-b border-dashed border-border bg-transparent py-2 text-right focus:outline-none"
                    placeholder="$/mo"
                    value={data[`bnpl${num}Amount`] || ''}
                    onChange={(e) => updateData(`bnpl${num}Amount`, e.target.value)}
                  />
                </div>
              ))}
            </div>
            <WorksheetInput
              label="Total BNPL payments per month:"
              placeholder="$___"
              value={data.totalBNPL || ''}
              onChange={(v) => updateData('totalBNPL', v)}
            />
          </div>
        </div>
      </div>
      
      <div className="my-8">
        <h3 className="font-semibold mb-4">Before moving on, confirm:</h3>
        <CheckboxItem checked={data.checklist1 || false} onChange={(v) => updateData('checklist1', v)}>
          I understand the 20/4/10 rule for cars
        </CheckboxItem>
        <CheckboxItem checked={data.checklist2 || false} onChange={(v) => updateData('checklist2', v)}>
          I know the risks of BNPL
        </CheckboxItem>
        <CheckboxItem checked={data.checklist3 || false} onChange={(v) => updateData('checklist3', v)}>
          I've tracked any current BNPL payments
        </CheckboxItem>
      </div>
      
      <RecapBox items={[
        "Car loans: 20% down, 4 years max, 10% of income max",
        "BNPL: Only use if you could pay cash today",
        "Track all BNPL payments—they add up fast",
        "Never miss payments (fees and credit damage)",
        "Borrow only for needs, not wants"
      ]} />
      
      <AffirmationBox>
        I borrow wisely and only when necessary. I don't let easy credit lead me into debt.
      </AffirmationBox>
      
      <OptionalSection>
        <WorksheetInput
          label="Your thoughts:"
          placeholder="Have you ever regretted a borrowing decision?"
          value={data.reflection || ''}
          onChange={(v) => updateData('reflection', v)}
          multiline
        />
      </OptionalSection>
    </WorkbookPage>
  );
};
